import "./CompanyCashClosings.css";
import ExcelJS from "exceljs";

import CashClosingReport from "./components/CashClosingReport";
import {
  openCashClosingPrintWindow,
} from "./printing/CashClosingPrinter";
import {
  useCompanyBranding,
} from "./CompanyBrandingContext";

import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

const initialFilters = {
  date_from: "",
  date_to: "",
  difference_status: "all",
  cashier: "all",
};

const CASH_MOVEMENT_LABELS = {
  opening: "Apertura",
  income: "Ingreso manual",
  sale_cash: "Venta en efectivo",
  expense: "Egreso manual",
  refund_cash: "Reintegro en efectivo",
  adjustment: "Ajuste",
};

function getHeaders() {
  return {
    headers: {
      Authorization: `Bearer ${localStorage.getItem(
        "company_token"
      )}`,
    },
  };
}

function formatCurrency(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDate(value) {
  return value
    ? new Date(value).toLocaleString("es-CO")
    : "No disponible";
}

function getErrorMessage(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg || "Dato inválido")
      .join(". ");
  }

  return "No fue posible completar la operación.";
}

function getCompanyLogoUrl(logo) {
  if (!logo) {
    return "";
  }

  if (
    logo.startsWith("http://") ||
    logo.startsWith("https://") ||
    logo.startsWith("data:")
  ) {
    return logo;
  }

  return `${API_URL}${logo.startsWith("/") ? logo : `/${logo}`}`;
}

function getExcelColor(color, fallback = "E6008D") {
  const normalized = String(color || "")
    .trim()
    .replace("#", "")
    .toUpperCase();

  return /^[0-9A-F]{6}$/.test(normalized)
    ? `FF${normalized}`
    : `FF${fallback}`;
}

function getLightExcelColor(color, fallback = "FCE7F3") {
  const normalized = String(color || "")
    .trim()
    .replace("#", "")
    .toUpperCase();

  if (!/^[0-9A-F]{6}$/.test(normalized)) {
    return `FF${fallback}`;
  }

  const mixWithWhite = (hexValue) => {
    const value = Number.parseInt(hexValue, 16);
    const lightValue = Math.round(value + (255 - value) * 0.88);

    return lightValue
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  };

  return `FF${mixWithWhite(
    normalized.slice(0, 2)
  )}${mixWithWhite(
    normalized.slice(2, 4)
  )}${mixWithWhite(normalized.slice(4, 6))}`;
}

async function getImageData(logoUrl) {
  if (!logoUrl) {
    return null;
  }

  let objectUrl = "";

  try {
    const separator = logoUrl.includes("?") ? "&" : "?";

    const freshLogoUrl =
      `${logoUrl}${separator}excel_export=${Date.now()}`;

    const response = await fetch(freshLogoUrl, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();

    objectUrl = URL.createObjectURL(blob);

    const image = await new Promise((resolve, reject) => {
      const logoImage = new Image();

      logoImage.onload = () => resolve(logoImage);
      logoImage.onerror = reject;
      logoImage.src = objectUrl;
    });

    const maxSize = 500;
    const scale = Math.min(
      1,
      maxSize / Math.max(image.naturalWidth, image.naturalHeight)
    );

    const canvas = document.createElement("canvas");

    canvas.width = Math.max(
      1,
      Math.round(image.naturalWidth * scale)
    );

    canvas.height = Math.max(
      1,
      Math.round(image.naturalHeight * scale)
    );

    const context = canvas.getContext("2d");

    context.drawImage(
      image,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return {
      dataUrl: canvas.toDataURL("image/png"),
      extension: "png",
    };
  } catch (error) {
    console.error("Error preparando logo para Excel:", error);
    return null;
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

function CompanyCashClosings() {
  const {
    branding,
    companyName: brandedCompanyName,
  } = useCompanyBranding();

  const [history, setHistory] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(true);

  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const loadHistory = useCallback(async (activeFilters = {}) => {
    try {
      setLoading(true);

      const params = {};

      if (activeFilters.date_from) {
        params.date_from = activeFilters.date_from;
      }

      if (activeFilters.date_to) {
        params.date_to = activeFilters.date_to;
      }

      const response = await axios.get(
        `${API_URL}/cash-register/history`,
        {
          ...getHeaders(),
          params,
        }
      );

      setHistory(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const visibleHistory = useMemo(() => {
    return history.filter((item) => {
      const difference = Number(item.difference_amount || 0);

      const matchesDifference =
        filters.difference_status === "all" ||
        (filters.difference_status === "balanced" &&
          difference === 0) ||
        (filters.difference_status === "difference" &&
          difference !== 0);

      const matchesCashier =
        filters.cashier === "all" ||
        item.opened_by === filters.cashier ||
        item.closed_by === filters.cashier;

      return matchesDifference && matchesCashier;
    });
  }, [
    history,
    filters.difference_status,
    filters.cashier,
  ]);

  const closingSummary = useMemo(() => {
    const balanced = visibleHistory.filter(
      (item) => Number(item.difference_amount || 0) === 0
    ).length;

    const withDifference =
      visibleHistory.length - balanced;

    const accumulatedDifference = visibleHistory.reduce(
      (total, item) =>
        total + Math.abs(Number(item.difference_amount || 0)),
      0
    );

    return {
      total: visibleHistory.length,
      balanced,
      withDifference,
      accumulatedDifference,
    };
  }, [visibleHistory]);

  const cashiers = useMemo(() => {
    const names = history.flatMap((item) => [
      item.opened_by,
      item.closed_by,
    ]);

    return [...new Set(names.filter(Boolean))].sort((first, second) =>
      first.localeCompare(second, "es")
    );
  }, [history]);

  const totalPages = Math.max(
    1,
    Math.ceil(visibleHistory.length / pageSize)
  );

  const paginatedHistory = useMemo(() => {
    const start = (currentPage - 1) * pageSize;

    return visibleHistory.slice(start, start + pageSize);
  }, [visibleHistory, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    history,
    filters.difference_status,
    filters.cashier,
    pageSize,
  ]);
  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSearch = (event) => {
    event.preventDefault();
    loadHistory(filters);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
    loadHistory();
  };

  const handleViewClosing = async (sessionId) => {
    try {
      setLoadingReport(true);

      const response = await axios.get(
        `${API_URL}/cash-register/report/${sessionId}`,
        getHeaders()
      );

      const companyInfo = JSON.parse(
        localStorage.getItem("company_info") || "{}"
      );

      setSelectedReport({
        ...response.data,
        company_logo:
          branding?.logo ||
          companyInfo.logo ||
          companyInfo.company_logo ||
          companyInfo.logo_url ||
          null,
        company_address: companyInfo.address || null,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        "No fue posible cargar el detalle del cierre."
      );
    } finally {
      setLoadingReport(false);
    }
  };

  const handlePrint = () => {
    const printed = openCashClosingPrintWindow(selectedReport);

    if (!printed) {
      toast.error(
        "No fue posible abrir la ventana de impresión. Verifica el bloqueador de ventanas emergentes."
      );
    }
  };

  const handleExportExcel = async () => {
    if (visibleHistory.length === 0) {
      toast.info("No hay cierres para exportar.");
      return;
    }

    try {
      setExporting(true);

      const companyInfo = JSON.parse(
        localStorage.getItem("company_info") || "{}"
      );

      const companyName =
        companyInfo.company_name ||
        companyInfo.name ||
        brandedCompanyName ||
        "Mi empresa";

      const companyColor =
        companyInfo.primary_color ||
        getComputedStyle(document.documentElement)
          .getPropertyValue("--company-primary")
          .trim();

      const primaryColor = getExcelColor(companyColor);
      const lightPrimaryColor = getLightExcelColor(companyColor);

      const workbook = new ExcelJS.Workbook();

      workbook.creator = "NetLedger";
      workbook.created = new Date();
      workbook.modified = new Date();

      const sidebarLogo = document.querySelector(
        ".company-sidebar-brand img"
      )?.src || "";

      const logoUrl = getCompanyLogoUrl(
        sidebarLogo ||
        branding?.logo ||
        companyInfo.logo ||
        companyInfo.company_logo ||
        companyInfo.logo_url ||
        ""
      );

      const logo = await getImageData(logoUrl);

      let logoImageId = null;

      if (logo?.dataUrl) {
        logoImageId = workbook.addImage({
          base64: logo.dataUrl.split(",")[1],
          extension: "png",
        });
      }

      const totalExpected = visibleHistory.reduce(
        (total, item) =>
          total + Number(item.expected_amount || 0),
        0
      );

      const totalCounted = visibleHistory.reduce(
        (total, item) =>
          total + Number(item.counted_amount || 0),
        0
      );

      const totalDifference = visibleHistory.reduce(
        (total, item) =>
          total + Number(item.difference_amount || 0),
        0
      );

      const balancedCount = visibleHistory.filter(
        (item) => Number(item.difference_amount || 0) === 0
      ).length;

      const summarySheet = workbook.addWorksheet(
        "Cierres de caja",
        {
          views: [{ state: "frozen", ySplit: 10 }],
          pageSetup: {
            orientation: "landscape",
            fitToPage: true,
            fitToWidth: 1,
            fitToHeight: 0,
          },
        }
      );

      summarySheet.properties.defaultRowHeight = 20;

      summarySheet.columns = [
        { width: 12 },
        { width: 23 },
        { width: 28 },
        { width: 28 },
        { width: 18 },
        { width: 18 },
        { width: 18 },
        { width: 20 },
        { width: 38 },
      ];

      summarySheet.mergeCells("A1:I1");
      summarySheet.getCell("A1").value =
        "REPORTE DE CIERRES DE CAJA";
      summarySheet.getCell("A1").font = {
        name: "Arial",
        size: 16,
        bold: true,
        color: { argb: "FFFFFFFF" },
      };
      summarySheet.getCell("A1").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: primaryColor },
      };
      summarySheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      summarySheet.getRow(1).height = 32;

      summarySheet.mergeCells("B2:I2");
      summarySheet.getCell("B2").value = companyName;
      summarySheet.getCell("B2").font = {
        name: "Arial",
        size: 13,
        bold: true,
        color: { argb: "FF18324D" },
      };

      summarySheet.mergeCells("B3:I3");
      summarySheet.getCell("B3").value = [
        companyInfo.nit ? `NIT: ${companyInfo.nit}` : "",
        companyInfo.address || "",
        companyInfo.phone || "",
        companyInfo.email || "",
      ]
        .filter(Boolean)
        .join(" · ");

      summarySheet.getCell("B3").font = {
        name: "Arial",
        size: 9,
        color: { argb: "FF64748B" },
      };

      summarySheet.mergeCells("B4:I4");
      summarySheet.getCell("B4").value = [
        filters.date_from
          ? `Desde: ${filters.date_from}`
          : "",
        filters.date_to
          ? `Hasta: ${filters.date_to}`
          : "",
      ]
        .filter(Boolean)
        .join(" · ") || "Histórico completo de cierres";

      summarySheet.getCell("B4").font = {
        name: "Arial",
        size: 9,
        italic: true,
        color: { argb: "FF64748B" },
      };

      summarySheet.mergeCells("B5:I5");
      summarySheet.getCell("B5").value =
        `Generado el ${new Date().toLocaleString(
          "es-CO"
        )} por NetLedger`;

      summarySheet.getCell("B5").font = {
        name: "Arial",
        size: 8,
        color: { argb: "FF94A3B8" },
      };

      if (logoImageId !== null) {
        summarySheet.addImage(logoImageId, {
          tl: { col: 0, row: 1 },
          br: { col: 1, row: 5 },
        });
      }

      const summaryItems = [
        ["Cierres", visibleHistory.length],
        ["Cajas cuadradas", balancedCount],
        ["Saldo esperado", totalExpected],
        ["Efectivo contado", totalCounted],
        ["Diferencia acumulada", totalDifference],
      ];

      summaryItems.forEach(([label, value], index) => {
        const column = index * 2 + 1;

        const titleCell = summarySheet.getCell(7, column);
        const valueCell = summarySheet.getCell(8, column);

        titleCell.value = label;
        valueCell.value = value;

        titleCell.font = {
          name: "Arial",
          size: 9,
          bold: true,
          color: { argb: "FF475569" },
        };

        valueCell.font = {
          name: "Arial",
          size: 11,
          bold: true,
          color: { argb: "FF18324D" },
        };

        titleCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: lightPrimaryColor },
        };

        valueCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF8FAFC" },
        };

        titleCell.alignment = {
          horizontal: "center",
          vertical: "middle",
        };

        valueCell.alignment = {
          horizontal: "center",
          vertical: "middle",
        };

        titleCell.border = {
          top: { style: "thin", color: { argb: "FFD8E2EA" } },
          left: { style: "thin", color: { argb: "FFD8E2EA" } },
          right: { style: "thin", color: { argb: "FFD8E2EA" } },
        };

        valueCell.border = {
          bottom: { style: "thin", color: { argb: "FFD8E2EA" } },
          left: { style: "thin", color: { argb: "FFD8E2EA" } },
          right: { style: "thin", color: { argb: "FFD8E2EA" } },
        };

        if (index >= 2) {
          valueCell.numFmt = '"$"#,##0';
        }
      });

      const headerRow = 10;

      summarySheet.getRow(headerRow).values = [
        "Caja",
        "Fecha de cierre",
        "Abierta por",
        "Cerrada por",
        "Saldo esperado",
        "Efectivo contado",
        "Diferencia",
        "Estado",
        "Observación",
      ];

      summarySheet.getRow(headerRow).eachCell((cell) => {
        cell.font = {
          name: "Arial",
          size: 9,
          bold: true,
          color: { argb: "FFFFFFFF" },
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: primaryColor },
        };

        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
        };

        cell.border = {
          top: { style: "thin", color: { argb: "FFFFFFFF" } },
          bottom: { style: "thin", color: { argb: "FFFFFFFF" } },
          left: { style: "thin", color: { argb: "FFFFFFFF" } },
          right: { style: "thin", color: { argb: "FFFFFFFF" } },
        };
      });

      visibleHistory.forEach((item, index) => {
        const rowNumber = headerRow + index + 1;
        const difference = Number(item.difference_amount || 0);
        const row = summarySheet.getRow(rowNumber);

        row.values = [
          `#${item.session_id}`,
          item.closed_at ? new Date(item.closed_at) : null,
          item.opened_by || "Sistema",
          item.closed_by || "Sistema",
          Number(item.expected_amount || 0),
          Number(item.counted_amount || 0),
          difference,
          difference === 0
            ? "Caja cuadrada"
            : "Con diferencia",
          item.closing_note || "",
        ];

        row.eachCell((cell) => {
          cell.font = {
            name: "Arial",
            size: 9,
            color: { argb: "FF334155" },
          };

          cell.alignment = {
            vertical: "middle",
            wrapText: cell.col === 9,
          };

          cell.border = {
            bottom: {
              style: "thin",
              color: { argb: "FFE2E8F0" },
            },
          };

          if (index % 2 === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF8FAFC" },
            };
          }
        });

        summarySheet.getCell(rowNumber, 2).numFmt =
          "dd/mm/yyyy hh:mm";

        [5, 6, 7].forEach((column) => {
          summarySheet.getCell(rowNumber, column).numFmt =
            '"$"#,##0';

          summarySheet.getCell(rowNumber, column).alignment = {
            horizontal: "right",
            vertical: "middle",
          };
        });

        summarySheet.getCell(rowNumber, 8).alignment = {
          horizontal: "center",
          vertical: "middle",
        };

        if (difference === 0) {
          summarySheet.getCell(rowNumber, 8).font = {
            name: "Arial",
            size: 9,
            bold: true,
            color: { argb: "FF047857" },
          };

          summarySheet.getCell(rowNumber, 8).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFD1FAE5" },
          };
        } else {
          summarySheet.getCell(rowNumber, 7).font = {
            name: "Arial",
            size: 9,
            bold: true,
            color: { argb: "FFDC2626" },
          };

          summarySheet.getCell(rowNumber, 8).font = {
            name: "Arial",
            size: 9,
            bold: true,
            color: { argb: "FFB91C1C" },
          };

          summarySheet.getCell(rowNumber, 8).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFEE2E2" },
          };
        }
      });

      summarySheet.autoFilter = {
        from: { row: headerRow, column: 1 },
        to: {
          row: headerRow + visibleHistory.length,
          column: 9,
        },
      };

      const summaryFooterRow =
        headerRow + visibleHistory.length + 2;

      summarySheet.mergeCells(
        `A${summaryFooterRow}:I${summaryFooterRow}`
      );

      summarySheet.getCell(`A${summaryFooterRow}`).value =
        "Documento de control interno generado por NetLedger.";

      summarySheet.getCell(`A${summaryFooterRow}`).font = {
        name: "Arial",
        size: 8,
        italic: true,
        color: { argb: "FF94A3B8" },
      };

      summarySheet.getCell(`A${summaryFooterRow}`).alignment = {
        horizontal: "center",
      };

      const detailResults = await Promise.allSettled(
        visibleHistory.map((item) =>
          axios.get(
            `${API_URL}/cash-register/report/${item.session_id}`,
            getHeaders()
          )
        )
      );

      const detailedReports = detailResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value.data);

      const failedReports = detailResults.filter(
        (result) => result.status === "rejected"
      ).length;

      if (failedReports > 0) {
        toast.info(
          `${failedReports} cierre(s) no pudieron incluirse en el detalle.`
        );
      }

      const detailSheet = workbook.addWorksheet(
        "Detalle de auditoría",
        {
          views: [{ state: "frozen", ySplit: 6 }],
          pageSetup: {
            orientation: "landscape",
            fitToPage: true,
            fitToWidth: 1,
            fitToHeight: 0,
          },
        }
      );

      detailSheet.properties.defaultRowHeight = 20;

      detailSheet.columns = [
        { width: 18 },
        { width: 28 },
        { width: 24 },
        { width: 24 },
        { width: 18 },
        { width: 16 },
        { width: 18 },
        { width: 22 },
        { width: 42 },
      ];

      detailSheet.mergeCells("A1:I1");
      detailSheet.getCell("A1").value =
        "DETALLE DE AUDITORÍA DE CIERRES DE CAJA";

      detailSheet.getCell("A1").font = {
        name: "Arial",
        size: 16,
        bold: true,
        color: { argb: "FFFFFFFF" },
      };

      detailSheet.getCell("A1").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: primaryColor },
      };

      detailSheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      detailSheet.getRow(1).height = 32;

      detailSheet.mergeCells("B2:I2");
      detailSheet.getCell("B2").value = companyName;
      detailSheet.getCell("B2").font = {
        name: "Arial",
        size: 13,
        bold: true,
        color: { argb: "FF18324D" },
      };

      detailSheet.mergeCells("B3:I3");
      detailSheet.getCell("B3").value =
        "Denominaciones, movimientos y observaciones de cada cierre.";

      detailSheet.getCell("B3").font = {
        name: "Arial",
        size: 9,
        italic: true,
        color: { argb: "FF64748B" },
      };

      if (logoImageId !== null) {
        detailSheet.addImage(logoImageId, {
          tl: { col: 0, row: 1 },
          br: { col: 1, row: 5 },
        });
      }

      let currentRow = 6;

      detailedReports.forEach((report) => {
        const difference = Number(report.difference_amount || 0);

        const status =
          difference === 0
            ? "Caja cuadrada"
            : difference > 0
              ? "Sobrante"
              : "Faltante";

        detailSheet.mergeCells(
          `A${currentRow}:I${currentRow}`
        );

        const titleCell = detailSheet.getCell(
          `A${currentRow}`
        );

        titleCell.value =
          `CIERRE DE CAJA #${report.session_id} · ${status}`;

        titleCell.font = {
          name: "Arial",
          size: 11,
          bold: true,
          color: { argb: "FFFFFFFF" },
        };

        titleCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb:
              difference === 0
                ? "FF047857"
                : "FFB91C1C",
          },
        };

        detailSheet.getRow(currentRow).height = 24;
        currentRow += 1;

        detailSheet.getRow(currentRow).values = [
          "Abierta por",
          report.opened_by || "Sistema",
          "Cerrada por",
          report.closed_by || "Sistema",
          "Saldo esperado",
          Number(report.expected_amount || 0),
          "Efectivo contado",
          Number(report.counted_amount || 0),
          difference,
        ];

        for (let column = 1; column <= 9; column += 1) {
          const cell = detailSheet.getCell(
            currentRow,
            column
          );

          cell.font = {
            name: "Arial",
            size: 9,
            bold: true,
            color: { argb: "FF334155" },
          };

          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF1F5F9" },
          };

          cell.border = {
            bottom: {
              style: "thin",
              color: { argb: "FFD8E2EA" },
            },
          };
        }

        [6, 8, 9].forEach((column) => {
          detailSheet.getCell(currentRow, column).numFmt =
            '"$"#,##0';
        });

        currentRow += 2;

        if (report.denominations?.length > 0) {
          detailSheet.mergeCells(
            `A${currentRow}:I${currentRow}`
          );

          detailSheet.getCell(`A${currentRow}`).value =
            "CONTEO POR DENOMINACIÓN";

          detailSheet.getCell(`A${currentRow}`).font = {
            name: "Arial",
            size: 10,
            bold: true,
            color: { argb: "FF18324D" },
          };

          currentRow += 1;

          detailSheet.getRow(currentRow).values = [
            "Denominación",
            "Cantidad",
            "Subtotal",
          ];

          for (let column = 1; column <= 3; column += 1) {
            const cell = detailSheet.getCell(
              currentRow,
              column
            );

            cell.font = {
              name: "Arial",
              size: 9,
              bold: true,
              color: { argb: "FFFFFFFF" },
            };

            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: primaryColor },
            };

            cell.alignment = {
              horizontal: "center",
            };
          }

          currentRow += 1;

          report.denominations.forEach((item) => {
            const denomination = Number(
              item.denomination || 0
            );

            const quantity = Number(item.quantity || 0);

            detailSheet.getRow(currentRow).values = [
              denomination,
              quantity,
              denomination * quantity,
            ];

            detailSheet.getCell(
              currentRow,
              1
            ).numFmt = '"$"#,##0';

            detailSheet.getCell(
              currentRow,
              3
            ).numFmt = '"$"#,##0';

            for (let column = 1; column <= 3; column += 1) {
              detailSheet.getCell(
                currentRow,
                column
              ).border = {
                bottom: {
                  style: "thin",
                  color: { argb: "FFE2E8F0" },
                },
              };
            }

            currentRow += 1;
          });

          currentRow += 1;
        }

        detailSheet.mergeCells(
          `A${currentRow}:I${currentRow}`
        );

        detailSheet.getCell(`A${currentRow}`).value =
          "MOVIMIENTOS REALIZADOS";

        detailSheet.getCell(`A${currentRow}`).font = {
          name: "Arial",
          size: 10,
          bold: true,
          color: { argb: "FF18324D" },
        };

        currentRow += 1;

        detailSheet.getRow(currentRow).values = [
          "Fecha",
          "Tipo",
          "Concepto",
          "Valor",
        ];

        for (let column = 1; column <= 4; column += 1) {
          const cell = detailSheet.getCell(
            currentRow,
            column
          );

          cell.font = {
            name: "Arial",
            size: 9,
            bold: true,
            color: { argb: "FFFFFFFF" },
          };

          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: primaryColor },
          };

          cell.alignment = {
            horizontal: "center",
          };
        }

        currentRow += 1;

        if (report.movements?.length > 0) {
          report.movements.forEach((movement) => {
            detailSheet.getRow(currentRow).values = [
              movement.created_at
                ? new Date(movement.created_at)
                : null,
              CASH_MOVEMENT_LABELS[movement.movement_type] ||
              movement.movement_type ||
              "Movimiento",
              movement.concept || "Sin concepto",
              Number(movement.amount || 0),
            ];

            detailSheet.getCell(
              currentRow,
              1
            ).numFmt = "dd/mm/yyyy hh:mm";

            detailSheet.getCell(
              currentRow,
              4
            ).numFmt = '"$"#,##0';

            for (let column = 1; column <= 4; column += 1) {
              detailSheet.getCell(
                currentRow,
                column
              ).border = {
                bottom: {
                  style: "thin",
                  color: { argb: "FFE2E8F0" },
                },
              };
            }

            currentRow += 1;
          });
        } else {
          detailSheet.mergeCells(
            `A${currentRow}:D${currentRow}`
          );

          detailSheet.getCell(`A${currentRow}`).value =
            "No se registraron movimientos adicionales.";

          detailSheet.getCell(`A${currentRow}`).font = {
            name: "Arial",
            size: 9,
            italic: true,
            color: { argb: "FF64748B" },
          };

          currentRow += 1;
        }

        if (report.closing_note) {
          detailSheet.mergeCells(
            `A${currentRow}:I${currentRow}`
          );

          const noteCell = detailSheet.getCell(
            `A${currentRow}`
          );

          noteCell.value =
            `Observación: ${report.closing_note}`;

          noteCell.font = {
            name: "Arial",
            size: 9,
            italic: true,
            color: { argb: "FF475569" },
          };

          noteCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF8FAFC" },
          };

          currentRow += 1;
        }

        currentRow += 2;
      });

      detailSheet.mergeCells(
        `A${currentRow}:I${currentRow}`
      );

      detailSheet.getCell(`A${currentRow}`).value =
        "Documento de control interno generado por NetLedger.";

      detailSheet.getCell(`A${currentRow}`).font = {
        name: "Arial",
        size: 8,
        italic: true,
        color: { argb: "FF94A3B8" },
      };

      detailSheet.getCell(`A${currentRow}`).alignment = {
        horizontal: "center",
      };

      const excelBuffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob(
        [excelBuffer],
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download =
        `reporte-cierres-caja-${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      toast.success("Reporte de Excel generado correctamente.");
    } catch (error) {
      console.error(error);

      toast.error(
        "No fue posible generar el archivo de Excel."
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="company-cash-closings">
      <section className="cash-closings-heading">
        <div>
          <p>CONTROL OPERATIVO</p>
          <h1>Cierres de caja</h1>
          <span>
            Consulta, audita y reimprime los arqueos registrados.
          </span>
        </div>

        <div className="cash-closings-heading-stat">
          <span>Cierres encontrados</span>
          <strong>{visibleHistory.length}</strong>
        </div>
      </section>

            <section className="cash-closings-summary-grid">
        <article className="cash-closings-summary-card">
          <span>Total de cierres</span>
          <strong>{closingSummary.total}</strong>
          <small>Según los filtros aplicados</small>
        </article>

        <article className="cash-closings-summary-card success">
          <span>Cajas cuadradas</span>
          <strong>{closingSummary.balanced}</strong>
          <small>Sin diferencias de efectivo</small>
        </article>

        <article className="cash-closings-summary-card warning">
          <span>Con diferencia</span>
          <strong>{closingSummary.withDifference}</strong>
          <small>Requieren revisión</small>
        </article>

        <article className="cash-closings-summary-card">
          <span>Diferencia acumulada</span>
          <strong>
            {formatCurrency(
              closingSummary.accumulatedDifference
            )}
          </strong>
          <small>Suma absoluta de diferencias</small>
        </article>
      </section>

      <section className="cash-closings-card">
        <form
          className="cash-closings-filters"
          onSubmit={handleSearch}
        >
          <div className="cash-closings-field">
            <label htmlFor="date_from">Desde</label>
            <input
              id="date_from"
              type="date"
              name="date_from"
              value={filters.date_from}
              onChange={handleFilterChange}
            />
          </div>

          <div className="cash-closings-field">
            <label htmlFor="date_to">Hasta</label>
            <input
              id="date_to"
              type="date"
              name="date_to"
              value={filters.date_to}
              onChange={handleFilterChange}
            />
          </div>

          <div className="cash-closings-field">
            <label htmlFor="difference_status">Estado</label>
            <select
              id="difference_status"
              name="difference_status"
              value={filters.difference_status}
              onChange={handleFilterChange}
            >
              <option value="all">Todos los cierres</option>
              <option value="balanced">Caja cuadrada</option>
              <option value="difference">Con diferencia</option>
            </select>
          </div>
          <div className="cash-closings-field">
            <label htmlFor="cashier">Responsable</label>

            <select
              id="cashier"
              name="cashier"
              value={filters.cashier}
              onChange={handleFilterChange}
            >
              <option value="all">Todos los responsables</option>

              {cashiers.map((cashier) => (
                <option key={cashier} value={cashier}>
                  {cashier}
                </option>
              ))}
            </select>
          </div>

          <div className="cash-closings-field cash-page-size">
            <label htmlFor="page_size">
              Registros por página
            </label>
            <select
              id="page_size"
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10 registros</option>
              <option value="25">25 registros</option>
              <option value="50">50 registros</option>
              <option value="100">100 registros</option>
            </select>
          </div>

          <div className="cash-closings-filter-actions">
            <button
              type="submit"
              className="btn-search-cash-closings"
            >
              Buscar
            </button>

            <button
              type="button"
              className="btn-clear-cash-closings"
              onClick={handleClearFilters}
            >
              Limpiar
            </button>

            <button
              type="button"
              className="btn-export-cash-closings"
              onClick={handleExportExcel}
              disabled={
                visibleHistory.length === 0 || exporting
              }
            >
              {exporting
                ? "Generando Excel..."
                : "Exportar Excel"}
            </button>
          </div>
        </form>
      </section>


      <section className="cash-closings-card">
        <div className="cash-closings-table-wrapper">
          <table className="cash-closings-table">
            <thead>
              <tr>
                <th>Caja</th>
                <th>Fecha de cierre</th>
                <th>Abierta por</th>
                <th>Cerrada por</th>
                <th>Esperado</th>
                <th>Contado</th>
                <th>Diferencia</th>
                <th>Acción</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="cash-closings-empty">
                    Cargando cierres de caja...
                  </td>
                </tr>
              ) : visibleHistory.length === 0 ? (
                <tr>
                  <td colSpan="8" className="cash-closings-empty">
                    No existen cierres para los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                paginatedHistory.map((item) => {
                  const difference = Number(
                    item.difference_amount || 0
                  );

                  return (
                    <tr key={item.session_id}>
                      <td>
                        <strong>#{item.session_id}</strong>
                      </td>
                      <td>{formatDate(item.closed_at)}</td>
                      <td>{item.opened_by}</td>
                      <td>{item.closed_by}</td>
                      <td>
                        {formatCurrency(item.expected_amount)}
                      </td>
                      <td>
                        {formatCurrency(item.counted_amount)}
                      </td>
                      <td>
                        <span
                          className={
                            difference === 0
                              ? "cash-difference-balanced"
                              : "cash-difference-alert"
                          }
                        >
                          {difference === 0
                            ? "Cuadrada"
                            : formatCurrency(difference)}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn-view-closing"
                          disabled={loadingReport}
                          onClick={() =>
                            handleViewClosing(item.session_id)
                          }
                        >
                          {loadingReport ? "Cargando..." : "Ver"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {visibleHistory.length > 0 && (
          <div className="cash-closings-pagination">
            <span>
              Mostrando {(currentPage - 1) * pageSize + 1}–
              {Math.min(
                currentPage * pageSize,
                visibleHistory.length
              )}{" "}
              de {visibleHistory.length} cierres
            </span>

            <div>
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() =>
                  setCurrentPage((previous) =>
                    Math.max(1, previous - 1)
                  )
                }
              >
                Anterior
              </button>

              <strong>
                Página {currentPage} de {totalPages}
              </strong>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((previous) =>
                    Math.min(totalPages, previous + 1)
                  )
                }
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </section>

      {selectedReport && (
        <div
          className="cash-closing-detail-backdrop"
          onClick={() => setSelectedReport(null)}
        >
          <section
            className="cash-closing-detail-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="cash-closing-detail-header">
              <div>
                <span>DETALLE DEL CIERRE</span>
                <h2>
                  Cierre de caja #{selectedReport.session_id}
                </h2>
              </div>

              <button
                type="button"
                className="btn-close-closing-detail"
                onClick={() => setSelectedReport(null)}
                aria-label="Cerrar detalle"
              >
                ×
              </button>
            </header>

            <div className="cash-closing-detail-content">
              <CashClosingReport report={selectedReport} />
            </div>

            <footer className="cash-closing-detail-actions">
              <button
                type="button"
                className="btn-print-closing"
                onClick={handlePrint}
              >
                Imprimir comprobante
              </button>

              <button
                type="button"
                className="btn-cash-secondary"
                onClick={() => setSelectedReport(null)}
              >
                Cerrar
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanyCashClosings;